// apps/web/src/pages/author/widget-config/static-note-config.tsx
//
// Phase 8 config sub-form for template `static-note` — drives the inline
// configSchema editor per authr-05. Field set mirrors
// packages/shared/src/schemas/widget-templates/static-note.ts AND the per-template
// walker at packages/shared/src/forms/per-template/static-note.ts (Plan 03).
//
// Sub-form fields use per-path useWatch only — NEVER top-level (RESEARCH
// §"Pattern 3"). No formState reads. This template has no useFieldArray (no
// arrays in the schema), so authr-03 is trivially satisfied.
//
// configSchema shape (verbatim):
//   body: string  (1-2000)
//   tone: 'info' | 'warning' | 'note'
//
// W11 — exports `defaultStaticNoteConfig(): StaticNoteConfig` factory.

import type { StaticNoteConfig } from "@the-recipe-spread/shared";
import { useFormContext } from "react-hook-form";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export interface StaticNoteConfigSubFormProps {
  instanceId: string;
  basePath: string;
}

// W11 — minimum-valid StaticNoteConfig per the configSchema (body min 1 char).
export function defaultStaticNoteConfig(): StaticNoteConfig {
  return {
    body: "Note text",
    tone: "info",
  };
}

const TONES = ["info", "warning", "note"] as const;

export function StaticNoteConfigSubForm({
  instanceId: _instanceId,
  basePath,
}: StaticNoteConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();

  return (
    <Fieldset legend="STATIC NOTE">
      <FormField
        control={control}
        name={`${basePath}.body`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.body`}>
            <FormLabel>Body (markdown)</FormLabel>
            <FormControl>
              <Textarea {...f} value={f.value ?? ""} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.tone`}
        render={({ field: f }) => (
          <FormItem data-field-path={`${basePath}.tone`}>
            <FormLabel>tone</FormLabel>
            <Select value={(f.value as string) ?? "info"} onValueChange={f.onChange}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="info" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {TONES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </Fieldset>
  );
}
StaticNoteConfigSubForm.displayName = "StaticNoteConfigSubForm";
